import AllocateSalesOrderLinesMutation from '../../graphql/mutations/sales/AllocateSalesOrderLinesMutation.graphql';
import DeallocateSalesOrderLinesMutation from '../../graphql/mutations/sales/DeallocateSalesOrderLinesMutation.graphql';
import UnReserveSalesOrderLinesMutation from '../../graphql/mutations/sales/UnReserveSalesOrderLinesMutation.graphql';
import ManualPriceApprovedToggleMutation from '../../graphql/mutations/sales/ManualPriceApprovedToggleMutation.graphql';
import SalesOrderLinesNotifyMaxoptraMutation from '../../graphql/mutations/sales/SalesOrderLinesNotifyMaxoptraMutation.graphql';
import React, { ReactElement, ReactNode, useContext, useState } from 'react';
import { useReturnOrderFromOrderMutation } from '../../utils/SalesOrderHooks';
import SalesOrderContext from '../../components/Context/SalesOrderContext';
import ModalContext from '../../components/Context/ModalContext';
import { useBasicWorkspaceAction } from '../../components/Workspace/WorkspaceActionButtons';
import { useDispatchAction } from '../../components/ActionButtons/dispatch/DispatchAction';
import { AllocateWorkspaceAction } from './AllocateWorkspaceAction';
import { useActionCriteria, useActionProps } from '../../utils/useActionProps';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { IDeprecatedWorkspaceAction, IWorkspaceAction } from '../../@types/workspaceTypes';
import { DispatchSource } from '../../components/ActionButtons/dispatch/DispatchModal';
import { useRefFunction } from '../../utils/hooks';
import CancelDispatchInstructionOrderLinesMutation from '../../graphql/mutations/sales/CancelDispatchInstructionOrderLines.graphql';
import { WorkspaceActionHeader } from '../../components/ActionButtons/ActionHeader';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { Analytics } from '../../mixpanel';
import { useRefreshMaxoptraTrackingForSalesOrderLines } from './MaxoptraNotification';
import { useNotifyMaxoptra } from './SalesOrderWorkspaceAction';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import getClientMutationId from '../../utils/getClientMutationId';
import { Field, FluentProvider, Radio, RadioGroup, webLightTheme } from '@fluentui/react-components';
import FluentModal from '../../components/modals/FluentModal';
import { BasicHeader } from '../BasicHeader';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { PrimaryButton, SecondaryButton } from '../../components/connectedForm/Button';
import {
  AlertType,
  CustomerSpecialPriceUpdateOption,
  ISalesOrderLinePriceApprovedToggleMutationResult,
  ISalesOrderLinePriceApprovedToggleMutationVariables,
  InputMaybe,
} from '../../graphql/schema/p4types';
import { useP4ToastController } from '../../views/system/NotificationsArea';

export const workspaceActionsForPermissions = [
  {
    actionReference: 'SALES_PRODUCT_ALLOCATION',
  },
  {
    actionReference: 'SALES_PRODUCT_DEALLOCATION',
  },
  {
    actionReference: 'SALES_ORDER_UNRESERVE',
  },
  {
    actionReference: 'SALES_ORDER_LINES_CANCEL_DISPATCH',
  },
  {
    description: 'Approve/Unapprove Manual Price',
    actionReference: 'SALES_ORDER_LINES_MANUAL_PRICE_APPROVAL',
  },
  {
    actionReference: 'SALES_ORDER_LINES_NOTIFY_MAXOPTRA',
  },
  {
    actionReference: 'SALES_ORDER_LINES_REFRESH_MAXOPTRA_TRACKING',
  },
];

const useReturnOrderFromLinesAction = () => {
  const salesOrderContext = useContext(SalesOrderContext);
  const { onClose } = useContext(ModalContext);
  const actionProps = useActionProps('originLines');
  const { raiseReturn } = useReturnOrderFromOrderMutation();
  const connectedForm = useContext(ConnectedFormContext);

  return {
    key: 'raiseReturn',
    onClick: () => {
      raiseReturn(actionProps);
      onClose?.(); //Close the line workspaceActionsForPermissions modal (This action is only displayed when in a modal from the sales order form.
    },
    text: 'Raise Return',
    actionReference: 'SALES_LEDGER_INVOICES_RETURN_ORDER',
    condition: () =>
      !!salesOrderContext?.setReturnOrderModalState &&
      (connectedForm.values?.salesOrderStatus.includes('DISPATCHED') ||
        connectedForm.values?.salesOrderStatus.includes('INVOICED')),
  };
};

const useNotifyMaxoptraFromLinesAction = (): [IWorkspaceAction, ReactElement] => {
  return useNotifyMaxoptra(
    SalesOrderLinesNotifyMaxoptraMutation,
    'SALES_ORDER_LINES_NOTIFY_MAXOPTRA',
    'notifyMaxoptraForSalesOrderLines'
  );
};

const useRefreshMaxoptraTrackingFromLinesAction = () => {
  const refreshMaxoptraTracking = useRefreshMaxoptraTrackingForSalesOrderLines();
  const actionCriteria = useActionCriteria();

  return {
    key: 'refreshMaxoptraTrackingForSalesOrder',
    onClick: () => {
      refreshMaxoptraTracking(actionCriteria);
    },
    text: 'Refresh Tracking',
    actionReference: 'SALES_ORDER_LINES_REFRESH_MAXOPTRA_TRACKING',
    header: WorkspaceActionHeader.MAXOPTRA,
  };
};

type ICustomerSpecialPriceUpdateOptions =
  | CustomerSpecialPriceUpdateOption.None
  | CustomerSpecialPriceUpdateOption.Invoice
  | CustomerSpecialPriceUpdateOption.PriceSource
  | CustomerSpecialPriceUpdateOption.Delivery;

const useToggleManualPriceApprovalAction = (): [IWorkspaceAction, ReactElement] => {
  const connectedForm = useContext(ConnectedFormContext);
  const workspace = useContext(WorkspaceContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ICustomerSpecialPriceUpdateOptions>(
    CustomerSpecialPriceUpdateOption.None
  );
  const workspaceContext = useContext(WorkspaceContext);
  const actionProps = useActionCriteria();
  const { dispatchSimpleToast } = useP4ToastController();

  const dispatchErrorToast = () => {
    dispatchSimpleToast(
      'Approve/Unapprove Manual Prices',
      'Could not toggle price approval for all selected lines',
      AlertType.Warning
    );
  };

  const handleError = (data: any) => {
    //Reload the form to refresh any records that have updated.
    workspace.reloadData?.();
    connectedForm.reloadData?.(true);
    dispatchErrorToast();
  };

  const handleCompleted = () => {
    //Reload the form to refresh status/prevent merge conflicts.
    connectedForm.reloadData?.(true);
    workspace.reloadData?.();
    Analytics.track('Approve/Unapprove Manual Price Toggle', {
      SelectedRows: workspace?.workspaceSelection?.selectedRows,
    });
  };

  const [runSalesOrderLinePriceApprovedToggle] = useBackgroundMutation<
    ISalesOrderLinePriceApprovedToggleMutationResult,
    ISalesOrderLinePriceApprovedToggleMutationVariables
  >(ManualPriceApprovedToggleMutation, {
    jobTitle: 'Approve/Unapprove Manual Prices',
    onStartJob: () => {
      setModalOpen(false);
    },
    onFailedJob: handleError,
    onCompleteJob: handleCompleted,
    onError: dispatchErrorToast,
  });

  const toggleManualPriceApprovalAction: IWorkspaceAction = {
    key: 'toggleManualPriceApproval',
    actionReference: 'SALES_ORDER_LINES_MANUAL_PRICE_APPROVAL',
    text: 'Approve/Unapprove Manual Price',
    onClick: () => setModalOpen(true),
  };

  const onClickConfirmManualPriceApproval = () => {
    runSalesOrderLinePriceApprovedToggle({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          actionCriteria: actionProps,
          option: selectedOption as InputMaybe<CustomerSpecialPriceUpdateOption>,
        },
      },
    });
  };

  const modals = (
    <FluentModal isOpen={modalOpen} styles={{ main: { paddingBottom: '60px' } }}>
      <BasicHeader>Manual Price Approval Toggle ({workspaceContext.selectionState.count ?? 0} Records)</BasicHeader>
      <div style={{ padding: '1em' }}>
        <FluentProvider key={'customer-special-price-update-option'} theme={webLightTheme}>
          <Field label={'Do you want to update customer special prices from approved manual prices?'}>
            <RadioGroup
              layout={'horizontal'}
              defaultValue={CustomerSpecialPriceUpdateOption.None}
              onChange={(_, data) => {
                setSelectedOption(data.value as ICustomerSpecialPriceUpdateOptions);
              }}
            >
              <Radio value={CustomerSpecialPriceUpdateOption.None} label={'No'} />
              <Radio value={CustomerSpecialPriceUpdateOption.Invoice} label={'Update for Invoice Account'} />
              <Radio value={CustomerSpecialPriceUpdateOption.Delivery} label={'Update for Customer Account'} />
              <Radio value={CustomerSpecialPriceUpdateOption.PriceSource} label={'Update for Price Source'} />
            </RadioGroup>
          </Field>
        </FluentProvider>
      </div>
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={() => setModalOpen(false)}>Cancel</SecondaryButton>
        <PrimaryButton onClick={onClickConfirmManualPriceApproval}>Confirm</PrimaryButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );

  return [toggleManualPriceApprovalAction, modals];
};

export const useSalesOrderLineWorkspaceActions = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const [toggleManualPriceApprovalAction, toggleManualPriceApprovalModal] = useToggleManualPriceApprovalAction();

  const dispatchCondition = useRefFunction(() => {
    return !connectedForm.values?.archived;
  });
  const [dispatchActionButton, printModals] = useDispatchAction({
    source: DispatchSource.SalesOrderLineWorkspace,
    condition: dispatchCondition,
  });

  const cancelDispatchActionButton = useBasicWorkspaceAction({
    mutation: CancelDispatchInstructionOrderLinesMutation,
    type: 'salesOrderLines',
    errorMessage: 'Could not cancel dispatch for order lines',
    text: 'Cancel Dispatch',
    header: WorkspaceActionHeader.PROCESS_ORDERS,
    actionReference: 'SALES_ORDER_LINES_CANCEL_DISPATCH',
    condition: () => !connectedForm.values?.archived,
    requiresActionCriteria: true,
    onComplete: () => {
      connectedForm.reloadData?.(true);
    },
  });

  const returnAction = useReturnOrderFromLinesAction();
  const deallocateAction = useBasicWorkspaceAction({
    mutation: DeallocateSalesOrderLinesMutation,
    type: 'salesOrderLines',
    errorMessage: 'Could not deallocate order lines',
    text: 'Deallocate',
    actionReference: 'SALES_PRODUCT_DEALLOCATION',
    condition: () => !connectedForm.values?.archived,
  });
  const unreserveAction = useBasicWorkspaceAction({
    mutation: UnReserveSalesOrderLinesMutation,
    type: 'salesOrderLines',
    errorMessage: 'Could not unreserve order lines',
    text: 'Unreserve',
    actionReference: 'SALES_ORDER_UNRESERVE',
    condition: () => !connectedForm.values?.archived,
    onComplete: () => {
      //Unreserving will update concurrencyVersion on the Sales Order, so we need to reload the form to refresh status/prevent merge conflicts.
      connectedForm.reloadData?.(true);
    },
  });
  const [notifyMaxoptraAction, notifyMaxoptraModal] = useNotifyMaxoptraFromLinesAction();
  const refreshMaxoptraTrackingAction = useRefreshMaxoptraTrackingFromLinesAction();

  const actions: [Array<IWorkspaceAction | IDeprecatedWorkspaceAction>, ReactNode | ReactElement] = [
    [
      {
        actionReference: 'SALES_PRODUCT_ALLOCATION',
        element: (
          <AllocateWorkspaceAction
            mutation={AllocateSalesOrderLinesMutation}
            type={'salesOrderLines'}
            title={'Allocating Lines'}
          />
        ),
        condition: () => !connectedForm.values?.archived,
      },
      deallocateAction,
      unreserveAction,
      returnAction,
      dispatchActionButton,
      cancelDispatchActionButton,
      toggleManualPriceApprovalAction,
      notifyMaxoptraAction,
      refreshMaxoptraTrackingAction,
    ],
    <>
      {printModals}
      {toggleManualPriceApprovalModal}
      {notifyMaxoptraModal}
    </>,
  ];

  return actions;
};
